import React, { useContext, useEffect, useState } from 'react';
import { Menu, Sidebar } from 'react-pro-sidebar';
import { useNavigate } from 'react-router-dom';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';

import {
  AppTitle,
  BottomLinks,
  LogoText,
  SidebarContainer,
  StyledLogo,
  Subtitle,
} from './styles';
import Logo from '../../../assets/Logo';
import FeedbackModal from '../../features/common_modals/FeedbackModal/FeedbackModal';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore, useContextStore } from '../../../stores/storeRoot';
import {
  administrationPath,
  individualDocumentsAllPath,
  individualDocumentsAnbauempfehlungPath,
  individualDocumentsLiefermitteilungenPath,
  individualDocumentsNewslettersPath,
  masterdataPath,
  myCampaignPath,
  myContactsPath,
  myContractsPath,
  myFieldsYieldsPath,
  myPaymentsPath,
  orderedDeliveredProductsPath,
  rootPath,
  userSettingsPath,
} from '../../../routes';
import StartIcon from '../../../assets/icons/sidebarIcons/StartIcon';
import MyContractsIcon from '../../../assets/icons/sidebarIcons/MyContractsIcon';
import MyOrdersIcon from '../../../assets/icons/sidebarIcons/MyOrdersIcon';
import MyCampaignIcon from '../../../assets/icons/sidebarIcons/MyCampaignIcon';
import MyPaymentsIcon from '../../../assets/icons/sidebarIcons/MyPaymentsIcon';
import MyFieldsYieldsIcon from '../../../assets/icons/sidebarIcons/MyFieldsYieldsIcon';
import MasterdataIcon from '../../../assets/icons/sidebarIcons/MasterdataIcon';
import UserIcon from '../../../assets/icons/sidebarIcons/UserIcon';
import LogoutIcon from '../../../assets/icons/sidebarIcons/LogoutIcon';
import MenuItemLink from './MenuItemLink';
import { TranslationKeys } from '../../../i18n/locales/defaultResources';
import { SelectedNewsContext } from '../../../context/SelectedNewsContext';
import MyContactsIcon from '../../../assets/icons/sidebarIcons/MyContacts';
import SecurityTrim, {
  securityTrimConditionsMet,
  SecurityTrimMode,
} from '../../../services/auth/SecurityTrim';
import { CropConnectRoleDefinitions } from '../../../services/cropConnectApiClient';
import { myFieldsAndYieldsRequiredRoleDefinitions } from '../../../services/auth/predefinedPermissions/predefinedRoleDefinitionsGroups';
import SettingsIcon from '../../../assets/icons/SettingsIcon';
import AgriPartnerIcon from '../../../assets/icons/sidebarIcons/AgriPartnerIcon';
import { sideBarAdministrationPathSecurityTrimProps } from '../../../services/auth/predefinedPermissions/predefinedSecurityTrimProps';

// Important Note: When modifying navigation logic in Sidebar, remember to update MobileMenu.tsx as well

const CustomSidebar: React.FC = () => {
  const { t } = useI18n();
  const {
    user,
    activeBpNumber,
    globalAssignment,
    businessPartnerAssignment,
    nordzuckerInternalUser,
    userRolesLoaded,
  } = useContextStore();
  const { logout } = useAuthStore();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const toggleFeedbackModal = () =>
    setIsFeedbackModalOpen(!isFeedbackModalOpen);
  const { setSelectedArticle } = useContext(SelectedNewsContext);
  const [isDocumentPublisher, setIsDocumentPublisher] = useState(false);

  const navigate = useNavigate();

  const handleHomePageClick = () => {
    setSelectedArticle(null);
    navigate(rootPath, { state: { reset: true } });
  };

  const handleAgriPartnerRedirect = () => {
    const agriPartnerUrl = process.env.REACT_APP_SSO_INTEGRATION_PL;
    if (agriPartnerUrl) {
      window.open(agriPartnerUrl, '_blank');
    }
  };

  useEffect(() => {
    if (userRolesLoaded) {
      setIsDocumentPublisher(
        securityTrimConditionsMet(
          {
            requiredGlobalRoles: [CropConnectRoleDefinitions.DocumentPublisher],
          },
          globalAssignment,
          businessPartnerAssignment,
          nordzuckerInternalUser
        )
      );
    }
  }, [userRolesLoaded]);

  return (
    <SidebarContainer>
      <Sidebar>
        <Menu>
          <StyledLogo>
            <Logo />
            <LogoText>
              <AppTitle>CropConnect</AppTitle>
              <Subtitle>by Nordzucker</Subtitle>
            </LogoText>
          </StyledLogo>
          <AuthenticatedTemplate>
            <MenuItemLink
              to={rootPath}
              subLinks={[
                individualDocumentsAllPath,
                individualDocumentsNewslettersPath,
              ]}
              icon={<StartIcon />}
              onClick={handleHomePageClick}
              label={t(TranslationKeys.sidebar_home)}
            />
            {(activeBpNumber || isDocumentPublisher) && (
              <>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                  requiredGlobalRoles={[
                    CropConnectRoleDefinitions.DocumentPublisher,
                  ]}
                  securityTrimMode={SecurityTrimMode.OR}
                >
                  <MenuItemLink
                    to={myContractsPath}
                    icon={<MyContractsIcon />}
                    label={t(TranslationKeys.sidebar_myContracts)}
                    subLinks={[individualDocumentsAnbauempfehlungPath]}
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItemLink
                    to={orderedDeliveredProductsPath}
                    icon={<MyOrdersIcon />}
                    label={t(TranslationKeys.sidebar_myOrders)}
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                  requiredGlobalRoles={[
                    CropConnectRoleDefinitions.DocumentPublisher,
                  ]}
                  securityTrimMode={SecurityTrimMode.OR}
                >
                  <MenuItemLink
                    to={myCampaignPath}
                    icon={<MyCampaignIcon />}
                    label={t(TranslationKeys.sidebar_myCampaign)}
                    subLinks={[individualDocumentsLiefermitteilungenPath]}
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                  requiredGlobalRoles={[
                    CropConnectRoleDefinitions.DocumentPublisher,
                  ]}
                  securityTrimMode={SecurityTrimMode.OR}
                >
                  <MenuItemLink
                    to={myPaymentsPath}
                    icon={<MyPaymentsIcon />}
                    label={t(TranslationKeys.sidebar_myPayments)}
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={
                    myFieldsAndYieldsRequiredRoleDefinitions
                  }
                  requiredGlobalRoles={[
                    CropConnectRoleDefinitions.DocumentPublisher,
                  ]}
                  securityTrimMode={SecurityTrimMode.OR}
                >
                  <MenuItemLink
                    to={myFieldsYieldsPath}
                    icon={<MyFieldsYieldsIcon />}
                    label={t(TranslationKeys.sidebar_myFieldsYields)}
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItemLink
                    to={myContactsPath}
                    icon={<MyContactsIcon />}
                    label={t(TranslationKeys.sidebar_contacts)}
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower,
                  ]}
                >
                  <MenuItemLink
                    to={masterdataPath}
                    icon={<MasterdataIcon />}
                    label={t(TranslationKeys.sidebar_masterdata)}
                  />
                </SecurityTrim>
                <SecurityTrim
                  requiredBusinessPartnerRoles={[
                    CropConnectRoleDefinitions.Grower_PL,
                  ]}
                >
                  <MenuItemLink
                    onClick={handleAgriPartnerRedirect}
                    icon={<AgriPartnerIcon />}
                    label={t(TranslationKeys.sidebar_agripartner_link)}
                  />
                </SecurityTrim>
              </>
            )}
            <BottomLinks>
              <SecurityTrim {...sideBarAdministrationPathSecurityTrimProps}>
                <MenuItemLink
                  label={t(TranslationKeys.administrationPage_title)}
                  icon={<SettingsIcon isSidebarIcon />}
                  to={administrationPath}
                />
              </SecurityTrim>
              <MenuItemLink
                to={userSettingsPath}
                icon={<UserIcon />}
                label={
                  user?.displayName ||
                  t(TranslationKeys.usermenu_item_username_placeholder)
                }
              />
              <MenuItemLink
                onClick={logout}
                icon={<LogoutIcon />}
                label={t(TranslationKeys.usermenu_item_logout)}
              />
            </BottomLinks>
          </AuthenticatedTemplate>
          <UnauthenticatedTemplate>
            <MenuItemLink
              to={rootPath}
              icon={<StartIcon />}
              label={t(TranslationKeys.sidebar_home)}
            />
          </UnauthenticatedTemplate>
        </Menu>
        {isFeedbackModalOpen && (
          <FeedbackModal
            title="Send Feedback"
            open={isFeedbackModalOpen}
            onClose={toggleFeedbackModal}
            onDiscard={toggleFeedbackModal}
          />
        )}
      </Sidebar>
    </SidebarContainer>
  );
};

export default CustomSidebar;
